'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { InputText } from '@/app/(football)/[team]/_components/common/InputText';
import { useAppDispatch } from '@/store/client/config';
import { modalActions } from '@/store/client/slices/modalSlice';
import { USE_FOOTBALL_QUERY_KEY, useRegisterOfflineEventMutation } from '@/store/server/common/football/football.query';

interface OfflineEventConfirmModalProps {
  methodIdx: number;
  methodCode: string;
}

const PASSWORD = '8';

export default function OfflineEventConfirmModal({ methodIdx, methodCode }: OfflineEventConfirmModalProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [password, setPassword] = useState('');
  const [passwordHelperText, setPasswordHelperText] = useState<{
    text: string;
    type: 'error' | 'info';
  }>({
    text: '',
    type: 'error',
  });

  const { mutate: registerOfflineEvent, isPending: isLoading } = useRegisterOfflineEventMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getPointSave] });
      handleOnClose();
    },
  });

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setPasswordHelperText({ text: '', type: 'error' });
  };

  const handleOnConfirm = () => {
    if (password !== PASSWORD) {
      setPasswordHelperText({ text: '비밀번호를 다시 확인해주세요', type: 'error' });
      return;
    }
    registerOfflineEvent({ methodIdx, methodCode });
  };

  const handleOnClose = () => {
    dispatch(modalActions.closeModal());
  };

  return (
    <div className="flex w-[320px] flex-col items-center rounded-lg bg-white p-6">
      {/* Icon */}
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 10V18" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="16" cy="22" r="1.5" fill="#EF4444" />
        </svg>
      </div>

      {/* Title */}
      <h2 className="mt-4 text-[18px] leading-[24px] font-bold text-gray-900">수령 완료 처리 하시겠습니까?</h2>

      {/* Description */}
      <div className="mt-2 flex flex-col items-center">
        <p className="text-[14px] leading-[18px] font-medium text-gray-500">행사 당일 부스 현장에 방문하시면</p>
        <p className="text-[14px] leading-[18px] font-medium text-gray-500">담당자가 수령 완료 처리를</p>
        <p className="text-[14px] leading-[18px] font-medium text-gray-500">도와드릴 예정입니다.</p>
      </div>

      {/* Password Input */}
      <div className="mt-5 w-full">
        <InputText
          type="password"
          inputMode="numeric"
          maxLength={8}
          value={password}
          onChange={handleChangePassword}
          helperText={passwordHelperText}
          autoFocus
        />
      </div>

      {/* Buttons */}
      <div className="mt-6 flex w-full gap-2">
        <button onClick={handleOnClose} className="h-12 flex-1 rounded bg-gray-900 text-[14px] font-bold text-white">
          닫기
        </button>
        <button
          onClick={handleOnConfirm}
          disabled={isLoading}
          className="h-12 flex-1 rounded bg-red-500 text-[14px] font-bold text-white disabled:opacity-50"
        >
          {isLoading ? '처리중...' : '처리'}
        </button>
      </div>
    </div>
  );
}
